// client/src/utils/wallet.js

// Connexion à un wallet de navigateur (MetaMask, Rabby, ...) en LECTURE SEULE.
//
// Périmètre de sécurité, volontairement minimal :
//  - on n'appelle QUE `eth_requestAccounts`, qui rend l'adresse publique ;
//  - on ne signe RIEN (ni transaction, ni message) : aucune surface d'attaque ;
//  - la clé privée et la seed phrase ne quittent jamais l'extension, c'est
//    structurellement impossible via cette API.
//
// Découverte via EIP-6963 (recommandé par MetaMask) : les extensions annoncent
// leur provider par événement, ce qui évite la course sur `window.ethereum`
// quand plusieurs wallets sont installés et s'écrasent l'un l'autre.

const DISCOVERY_TIMEOUT_MS = 300;

/** Recense les wallets injectés. Renvoie [{ info, provider }]. */
export const discoverProviders = () =>
  new Promise((resolve) => {
    const found = new Map();
    const onAnnounce = (event) => {
      const { info, provider } = event.detail || {};
      if (info && provider && !found.has(info.uuid)) found.set(info.uuid, { info, provider });
    };

    window.addEventListener('eip6963:announceProvider', onAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    // Les annonces arrivent de façon synchrone ou quasi : on laisse un court
    // délai, puis on retombe sur window.ethereum si personne n'a répondu.
    setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce);
      const providers = [...found.values()];
      if (providers.length === 0 && window.ethereum) {
        providers.push({
          info: { uuid: 'legacy', name: 'Wallet du navigateur', rdns: 'legacy' },
          provider: window.ethereum
        });
      }
      resolve(providers);
    }, DISCOVERY_TIMEOUT_MS);
  });

/**
 * Demande à l'utilisateur d'autoriser la lecture de son adresse.
 * `preferred` : sous-chaîne du nom du wallet à privilégier (ex. 'metamask').
 * Renvoie { address, walletName }.
 */
export const connectWallet = async (preferred = 'metamask') => {
  const providers = await discoverProviders();
  if (providers.length === 0) {
    throw new Error(
      "Aucun wallet détecté dans ce navigateur. Installez MetaMask ou saisissez l'adresse à la main."
    );
  }

  const match =
    providers.find((p) => (p.info.rdns || '').toLowerCase().includes(preferred)) ||
    providers.find((p) => (p.info.name || '').toLowerCase().includes(preferred)) ||
    providers[0];

  let accounts;
  try {
    // Seul appel effectué : rend la liste des adresses autorisées.
    accounts = await match.provider.request({ method: 'eth_requestAccounts' });
  } catch (err) {
    // 4001 = l'utilisateur a refusé dans la fenêtre du wallet.
    if (err && err.code === 4001) throw new Error('Connexion refusée dans le wallet.');
    throw new Error(err && err.message ? err.message : 'Connexion au wallet impossible.');
  }

  if (!accounts || accounts.length === 0) {
    throw new Error("Le wallet n'a renvoyé aucune adresse.");
  }

  return { address: String(accounts[0]).toLowerCase(), walletName: match.info.name };
};

/** Abrège une adresse pour l'affichage : 0x1234…abcd */
export const shortAddress = (address) =>
  !address || address.length < 12 ? address : `${address.slice(0, 6)}…${address.slice(-4)}`;
